import { useEffect, useRef, useState } from "react";
import { LngLatBounds, Map as MLMap, NavigationControl, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { FeatureCollection as GJFeatureCollection } from "geojson";
import { tryApiGet } from "../api/client";
import { buildBackboneMapStyle, MAP_CONTAINER_BACKGROUND } from "../lib/mapStyle";
import { FIRST_CALCULATION_TEXT } from '../lib/definitions/analysis';
import { formatDistanceBoth, type DistanceUnitPref } from "../lib/units";
import {
  REACH_CAPTION,
  REACH_DEFAULT_WINDOW,
  REACH_EMPTY_TEXT,
  REACH_LAST_HEARD_HEADER,
  REACH_LIST_SHOWN,
  REACH_RING_COUNT,
  REACH_TITLE,
  REACH_WINDOWS,
  reachDotTip,
  reachFurthestLineLabel,
  reachFiguresLine,
  reachHopColorVar,
  reachHopsText,
  reachLastHour,
  reachLegend,
  reachListOrder,
  reachListToggleLabel,
  reachNotHeardBy,
  reachPacketsHeader,
  reachRingCountsLine,
  reachRingLabel,
  reachRingLayout,
  reachRingOffset,
  reachRingStep,
  type ReachBlock,
  type ReachListener,
  type ReachTrends,
  type ReachWindow,
} from "../lib/reach";
import { tzAbbrev } from "../lib/rfHealth";
import { SectionCard } from "./SectionCard";
import { NodeTrendsCard } from "./NodeTrends";
import { trendsHasData } from "../lib/trends";
import { ResponsiveRecordTable } from "./ResponsiveRecordTable";

const RING_SIZE = 320;

function cssColor(name: string): string {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888888";
  } catch {
    return "#888888";
  }
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

interface Pos {
  lat: number;
  lon: number;
}

function ReachMap({ position, block, theme, nodeName }: { position: Pos | null; block: ReachBlock; theme: string; nodeName: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const placed = block.listeners.filter((l) => l.lat !== null && l.lon !== null);

  useEffect(() => {
    if (!ref.current) return;
    if (!position && placed.length === 0) return;
    const map = new MLMap({
      container: ref.current,
      style: buildBackboneMapStyle(),
      center: position ? [position.lon, position.lat] : [placed[0].lon as number, placed[0].lat as number],
      zoom: 8,
      attributionControl: { compact: true },
    });
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    const popup = new Popup({ closeButton: false, closeOnClick: false, offset: 10 });
    const nodeColor = cssColor("--ink");

    map.on("load", () => {
      const dots: GJFeatureCollection = {
        type: "FeatureCollection",
        features: placed.map((l) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [l.lon as number, l.lat as number] },
          properties: { tip: reachDotTip(l), color: cssColor(reachHopColorVar(l.min_hops)) },
        })),
      };
      const far = block.furthest ? placed.find((l) => l.key === block.furthest?.key) : undefined;
      const line: GJFeatureCollection = {
        type: "FeatureCollection",
        features: position && far
          ? [{
            type: "Feature",
            geometry: { type: "LineString", coordinates: [[position.lon, position.lat], [far.lon as number, far.lat as number]] },
            properties: { label: reachFurthestLineLabel(far.min_hops) },
          }]
          : [],
      };
      map.addSource("reach-line", { type: "geojson", data: line });
      // Dashed and labelled with the fewest relays: it joins two places, it is not an RF reach.
      map.addLayer({ id: "reach-line", type: "line", source: "reach-line", paint: { "line-color": nodeColor, "line-width": 1.2, "line-opacity": 0.8, "line-dasharray": [3, 2] } });
      map.addLayer({
        id: "reach-line-label", type: "symbol", source: "reach-line",
        layout: { "symbol-placement": "line-center", "text-field": ["get", "label"], "text-size": 11 } as never,
        paint: { "text-color": nodeColor, "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
      });
      map.addSource("reach-dots", { type: "geojson", data: dots });
      map.addLayer({
        id: "reach-dots", type: "circle", source: "reach-dots",
        paint: { "circle-radius": 6, "circle-color": ["get", "color"] as never, "circle-stroke-width": 1.5, "circle-stroke-color": "#ffffff" },
      });
      if (position) {
        map.addSource("reach-node", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Point", coordinates: [position.lon, position.lat] }, properties: { tip: nodeName } }] },
        });
        map.addLayer({
          id: "reach-node", type: "circle", source: "reach-node",
          paint: { "circle-radius": 8, "circle-color": nodeColor, "circle-stroke-width": 2.5, "circle-stroke-color": "#ffffff" },
        });
      }
      const show = (e: { features?: { properties?: Record<string, unknown>; geometry: { type: string; coordinates?: unknown } }[] }) => {
        const f = e.features?.[0];
        if (!f || f.geometry.type !== "Point") return;
        popup.setLngLat(f.geometry.coordinates as [number, number]).setHTML(`<div style="font-weight:700;">${esc(String(f.properties?.tip ?? ""))}</div>`).addTo(map);
      };
      for (const layer of position ? ["reach-dots", "reach-node"] : ["reach-dots"]) {
        map.on("mouseenter", layer, (e) => { map.getCanvas().style.cursor = "pointer"; show(e as never); });
        map.on("mousemove", layer, (e) => show(e as never));
        map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; popup.remove(); });
        map.on("click", layer, (e) => show(e as never));
      }
      const pts: [number, number][] = placed.map((l) => [l.lon as number, l.lat as number]);
      if (position) pts.push([position.lon, position.lat]);
      const bounds = new LngLatBounds(pts[0], pts[0]);
      for (const p of pts) bounds.extend(p);
      if (pts.length > 1) map.fitBounds(bounds, { padding: 40, duration: 0, maxZoom: 13 });
      else map.jumpTo({ center: pts[0], zoom: 11 });
    });
    return () => {
      popup.remove();
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block, position?.lat, position?.lon, theme]);

  if (!position && placed.length === 0) return null;
  return <div ref={ref} className="reach-map" style={{ background: MAP_CONTAINER_BACKGROUND.light }} />;
}

function ReachRings({ listeners }: { listeners: ReachListener[] }) {
  const [active, setActive] = useState<ReachListener | null>(null);
  const c = RING_SIZE / 2;
  const offset = reachRingOffset(listeners);
  const step = reachRingStep(listeners, RING_SIZE);
  const dots = reachRingLayout(listeners, RING_SIZE);
  const ringIds = Array.from({ length: REACH_RING_COUNT + offset }, (_, i) => i + 1 - offset);
  return (
    <div className="reach-rings">
      <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} role="img" aria-label="Reach rings">
        {ringIds.map((r) => (
          <g key={r}>
            <circle cx={c} cy={c} r={step * (r + offset)} fill="none" stroke="var(--border)" strokeWidth={1} />
            <text x={c + 5} y={c - step * (r + offset) + 11} textAnchor="start" fontSize={10} fill="var(--ink-muted)">{reachRingLabel(r)}</text>
          </g>
        ))}
        <circle cx={c} cy={c} r={7} fill="var(--ink)" stroke="var(--surface, #ffffff)" strokeWidth={2} />
        {dots.map((d) => (
          <circle
            key={d.listener.key}
            cx={d.x}
            cy={d.y}
            r={6}
            fill={`var(${reachHopColorVar(d.listener.min_hops)})`}
            stroke="var(--surface, #ffffff)"
            strokeWidth={1.5}
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setActive(d.listener)}
            onMouseLeave={() => setActive(null)}
            onClick={() => setActive(d.listener)}
          >
            <title>{reachDotTip(d.listener)}</title>
          </circle>
        ))}
      </svg>
      <div className="reach-rings-tip">{active ? reachDotTip(active) : " "}</div>
      <div className="reach-legend" aria-label="Ring colours">
        {reachLegend(listeners).map((x) => (
          <span key={x.ring} className="reach-legend-item">
            <span className="reach-legend-swatch" style={{ background: x.ring < 0 ? "var(--ink)" : `var(${reachHopColorVar(x.ring)})` }} />
            {x.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ReachObserverList({ listeners, win }: { listeners: ReachListener[]; win: ReachWindow }) {
  const [showAll, setShowAll] = useState(false);
  const ordered = reachListOrder(listeners);
  const rows = showAll ? ordered : ordered.slice(0, REACH_LIST_SHOWN);
  const columns = [
    { key: "observer", header: "Observer", mobileLabel: "Observer" },
    { key: "hops", header: <span className="rf-num">Min Hops</span>, mobileLabel: "Min Hops", className: "rf-num" },
    { key: "packets", header: <span className="rf-num">{reachPacketsHeader(win)}</span>, mobileLabel: reachPacketsHeader(win), className: "rf-num" },
    { key: "heard", header: REACH_LAST_HEARD_HEADER, mobileLabel: REACH_LAST_HEARD_HEADER },
  ];
  const tableRows = rows.map((l) => ({
    key: l.key,
    cells: {
      observer: <a href={`#/nodes/${encodeURIComponent(l.key)}`} className="node-name-link">{l.name}</a>,
      hops: l.min_hops <= 0 ? reachHopsText(0) : l.min_hops.toLocaleString("en-US"),
      packets: l.appearances.toLocaleString("en-US"),
      heard: reachLastHour(l.last_seen, tzAbbrev(l.last_seen)),
    },
  }));
  return (
    <>
      <ResponsiveRecordTable
        columns={columns}
        rows={tableRows}
        tableClassName="node-list-table reach-observers"
        ariaLabel="Reach observers"
      />
      {ordered.length > REACH_LIST_SHOWN && (
        <button type="button" className="topo-show-all" onClick={() => setShowAll((v) => !v)}>
          {reachListToggleLabel(ordered.length, showAll)}
        </button>
      )}
    </>
  );
}

export function NodeReachSection({
  nodeId, nodeName, position, theme, distPref,
}: { nodeId: string; nodeName: string; position: Pos | null; theme: string; distPref: DistanceUnitPref | undefined }) {
  const [win, setWin] = useState<ReachWindow>(REACH_DEFAULT_WINDOW);
  const [data, setData] = useState<ReachBlock | null | "none" | "pending" | "unavailable">(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    tryApiGet<ReachBlock | { pending: true }>(`/api/audit/reach?window=${win}&node=${encodeURIComponent(nodeId)}`)
      .then((r) => {
        if (cancelled) return;
        if (r === null) setData("none");
        else if ("pending" in r && r.pending) setData("pending");
        else setData(r as ReachBlock);
      })
      .catch(() => !cancelled && setData("unavailable"));
    return () => { cancelled = true; };
  }, [nodeId, win]);

  const block = data && typeof data === "object" ? data : null;
  const notHeard = block ? reachNotHeardBy(block) : [];
  const empty = data === "none" || (block !== null && block.listener_count === 0);

  return (
    <>
    <SectionCard title={REACH_TITLE}>
      <div className="theme-toggle" role="group" aria-label="Window" style={{ marginBottom: 10 }}>
        {REACH_WINDOWS.map((w) => (
          <button key={w} data-active={win === w} onClick={() => setWin(w)}>{w}</button>
        ))}
      </div>
      {data === null && <div className="node-detail-note">Loading…</div>}
      {data === "pending" && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {data === "unavailable" && <div className="node-detail-note">Unavailable</div>}
      {empty && <div className="node-detail-note">{REACH_EMPTY_TEXT}</div>}
      {block && block.listener_count > 0 && (
        <>
          <div className="reach-figures">{reachFiguresLine(block, (km) => formatDistanceBoth(km, distPref, 1))}</div>
          {notHeard.length > 0 && (
            <div className="reach-not-heard">
              {"Not heard by: "}
              {notHeard.map((o, i) => (
                <span key={o.key}>
                  {i > 0 && ", "}
                  <a href={`#/nodes/${encodeURIComponent(o.key)}`} className="node-name-link">{o.name}</a>
                </span>
              ))}
            </div>
          )}
          <div className="reach-ring-counts">{reachRingCountsLine(block.listeners)}</div>
          <div className="reach-caption">
            {REACH_CAPTION.map((item, index) => (
              <span key={item.label}>{index > 0 ? " · " : ""}<strong>{item.label}:</strong> {item.value}</span>
            ))}
          </div>
          <div className="reach-visuals">
            <ReachMap position={position} block={block} theme={theme} nodeName={nodeName} />
            <ReachRings listeners={block.listeners} />
          </div>
          <ReachObserverList listeners={block.listeners} win={win} />
        </>
      )}
    </SectionCard>
    {block && block.listener_count > 0 && trendsHasData(block.trends) && <NodeTrendsCard trends={block.trends as ReachTrends} windowKey={win} />}
    </>
  );
}
